<template>
  <div class="relative inline-block text-left">
    <!-- Bouton principal - plus petit et discret -->
    <button
      @click="toggleDropdown"
      class="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
      :class="{ 'ring-2 ring-blue-500': isOpen }"
    >
      <!-- Drapeau de la langue actuelle -->
      <span class="text-base mr-1">{{ getCurrentFlag() }}</span>

      <!-- Icône chevron plus petite -->
      <ChevronDownIcon
        :class="[
          'h-3 w-3 text-gray-400 transition-transform duration-200',
          isOpen ? 'rotate-180' : '',
        ]"
      />
    </button>

    <!-- Dropdown menu - plus compact -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 z-50 mt-1 w-32 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      >
        <div class="py-1">
          <button
            v-for="language in availableLanguages"
            :key="language.code"
            @click="selectLanguage(language.code)"
            class="group flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
            :class="{
              'bg-blue-50 text-blue-700': currentLanguage === language.code,
            }"
          >
            <span class="text-base mr-2">{{ language.flag }}</span>
            <span class="text-xs font-medium">{{ language.name }}</span>

            <!-- Indicateur de langue active -->
            <CheckIcon
              v-if="currentLanguage === language.code"
              class="ml-auto h-3 w-3 text-blue-600"
            />
          </button>
        </div>
      </div>
    </Transition>
  </div>

  <!-- Notification d'erreur -->
  <div
    v-if="i18nStore.error"
    class="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-sm"
  >
    {{ i18nStore.error }}
    <button @click="clearError" class="ml-2 text-red-500 hover:text-red-700 font-medium">✕</button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ChevronDownIcon, CheckIcon } from 'lucide-vue-next'
import { useI18nStore } from '@/stores/i18n'

// Store
const i18nStore = useI18nStore()

// État local
const isOpen = ref(false)

// Langues disponibles
const availableLanguages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
]

// Computed
const currentLanguage = computed(() => i18nStore.currentLanguage)

// Méthodes
function toggleDropdown() {
  isOpen.value = !isOpen.value
}

function selectLanguage(languageCode: string) {
  i18nStore.changeLanguage(languageCode as any)
  isOpen.value = false
}

function getCurrentFlag(): string {
  const current = availableLanguages.find(lang => lang.code === currentLanguage.value)
  return current?.flag || '🇫🇷'
}

function closeDropdown() {
  isOpen.value = false
}

function clearError(): void {
  i18nStore.error = null
}

// Fermer le dropdown quand on clique ailleurs
function handleClickOutside(event: Event) {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    closeDropdown()
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Styles additionnels si nécessaire */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Transitions pour le dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
